import { useState, useEffect } from "react";
import { Modal, Descriptions, Row, Col, Divider, Tag, Spin, Empty, Typography } from "antd";
import { PhoneOutlined, MailOutlined, UserOutlined, TeamOutlined, LoadingOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

const ContactUsModal = ({ open, onClose }) => {
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (open) {
      loadProjectInfo();
    }
  }, [open]);

  const loadProjectInfo = () => {
    setLoading(true);
    setError(false);
    const timestamp = new Date().getTime();

    fetch(`/static/config/projectInfo.json?t=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setProjectInfo(data);
        setLoading(false);
        setError(false);
      })
      .catch(error => {
        console.warn('Could not load project info:', error.message);
        setProjectInfo(null);
        setError(true);
        setLoading(false);
      });
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <TeamOutlined className="text-blue-600" />
          <span>Contact Information</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
    >
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} tip="Loading project information..." />
        </div>
      ) : error || !projectInfo ? (
        <div className="flex justify-center items-center py-12">
          <Empty
            description={
              <div>
                <Text type="secondary">No contact information available</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Please configure the project information in /public/static/config/projectInfo.json
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Project Details */}
          <div>
            <Title level={5} className="text-blue-600 mb-3">
              Project Details
            </Title>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div className="flex flex-col">
                  <Text type="secondary" className="text-xs mb-1">Project Name</Text>
                  <Tag color="blue" className="w-fit">{projectInfo.project}</Tag>
                </div>
              </Col>
              <Col span={12}>
                <div className="flex flex-col">
                  <Text type="secondary" className="text-xs mb-1">Plant Location</Text>
                  <Tag color="green" className="w-fit">{projectInfo.plant}</Tag>
                </div>
              </Col>
            </Row>
          </div>

          <Divider className="my-4" />

          {/* Site Engineer Contact */}
          <div>
            <Title level={5} className="text-blue-600 mb-3 flex items-center gap-2">
              <UserOutlined /> Site Engineer
            </Title>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Name">
                <Text strong>{projectInfo.siteEngineer.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={<span><PhoneOutlined className="mr-2" />Contact</span>}>
                <a href={`tel:${projectInfo.siteEngineer.contact}`} className="text-blue-600 hover:text-blue-800">
                  {projectInfo.siteEngineer.contact}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                <a href={`mailto:${projectInfo.siteEngineer.email}`} className="text-blue-600 hover:text-blue-800">
                  {projectInfo.siteEngineer.email}
                </a>
              </Descriptions.Item>
            </Descriptions>
          </div>

          <Divider className="my-4" />

          {/* Team Members */}
          <div>
            <Title level={5} className="text-blue-600 mb-3 flex items-center gap-2">
              <TeamOutlined /> Project Team
            </Title>

            <Row gutter={[16, 0]}>
              {/* Left Column */}
              <Col span={12}>
                {/* Team Lead */}
                <div className="mb-4">
                  <Text type="secondary" className="text-xs">Team Lead</Text>
                  <Descriptions column={1} size="small" bordered className="mt-1">
                    <Descriptions.Item label={<span><UserOutlined className="mr-2" />Name</span>}>
                      <Text strong>{projectInfo.team.lead.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                      <a href={`mailto:${projectInfo.team.lead.email}`} className="text-blue-600 hover:text-blue-800">
                        {projectInfo.team.lead.email}
                      </a>
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                {/* Technical Lead */}
                <div className="mb-4">
                  <Text type="secondary" className="text-xs">Technical Lead</Text>
                  <Descriptions column={1} size="small" bordered className="mt-1">
                    <Descriptions.Item label={<span><UserOutlined className="mr-2" />Name</span>}>
                      <Text strong>{projectInfo.team.technicalLead.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                      <a href={`mailto:${projectInfo.team.technicalLead.email}`} className="text-blue-600 hover:text-blue-800">
                        {projectInfo.team.technicalLead.email}
                      </a>
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                {/* Operations Lead */}
                <div className="mb-4">
                  <Text type="secondary" className="text-xs">Operations Lead</Text>
                  <Descriptions column={1} size="small" bordered className="mt-1">
                    <Descriptions.Item label={<span><UserOutlined className="mr-2" />Name</span>}>
                      <Text strong>{projectInfo.team.operationsLead.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                      <a href={`mailto:${projectInfo.team.operationsLead.email}`} className="text-blue-600 hover:text-blue-800">
                        {projectInfo.team.operationsLead.email}
                      </a>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>

              {/* Right Column */}
              <Col span={12}>
                {/* Frontend Developer */}
                <div className="mb-4">
                  <Text type="secondary" className="text-xs">Frontend Developer</Text>
                  <Descriptions column={1} size="small" bordered className="mt-1">
                    <Descriptions.Item label={<span><UserOutlined className="mr-2" />Name</span>}>
                      <Text strong>{projectInfo.team.frontend.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                      <a href={`mailto:${projectInfo.team.frontend.email}`} className="text-blue-600 hover:text-blue-800">
                        {projectInfo.team.frontend.email}
                      </a>
                    </Descriptions.Item>
                  </Descriptions>
                </div>

                {/* Backend Developer */}
                <div className="mb-4">
                  <Text type="secondary" className="text-xs">Backend Developer</Text>
                  <Descriptions column={1} size="small" bordered className="mt-1">
                    <Descriptions.Item label={<span><UserOutlined className="mr-2" />Name</span>}>
                      <Text strong>{projectInfo.team.backend.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                      <a href={`mailto:${projectInfo.team.backend.email}`} className="text-blue-600 hover:text-blue-800">
                        {projectInfo.team.backend.email}
                      </a>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
                {/* App Developer */}
                <div className="mb-4">
                  <Text type="secondary" className="text-xs">App Developer</Text>
                  <Descriptions column={1} size="small" bordered className="mt-1">
                    <Descriptions.Item label={<span><UserOutlined className="mr-2" />Name</span>}>
                      <Text strong>{projectInfo.team.appDeveloper.name}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={<span><MailOutlined className="mr-2" />Email</span>}>
                      <a href={`mailto:${projectInfo.team.appDeveloper.email}`} className="text-blue-600 hover:text-blue-800">
                        {projectInfo.team.appDeveloper.email}
                      </a>
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ContactUsModal;
